import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Stack,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Tooltip, Switch, alpha, useTheme,
  Card, CardContent, Grid, Avatar, TextField, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SearchIcon from '@mui/icons-material/Search';
import BrushIcon from '@mui/icons-material/Brush';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ImageIcon from '@mui/icons-material/Image';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchDesignPlans, deleteDesignPlan, updateDesignPlan } from '../designApi';

export default function DesignPlans() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchDesignPlans(showInactive);
      console.log('📦 fetchDesignPlans response:', data);

      // ✅ Handle all possible response structures:
      // Backend might return: [...] OR { plans: [...] } OR { data: [...] } OR { rows: [...] }
      let plansArray = [];
      if (Array.isArray(data)) {
        plansArray = data;
      } else if (data && Array.isArray(data.plans)) {
        plansArray = data.plans;
      } else if (data && Array.isArray(data.data)) {
        plansArray = data.data;
      } else if (data && Array.isArray(data.rows)) {
        plansArray = data.rows;
      }

      setPlans(plansArray);
    } catch (err) {
      console.error('Failed to load plans:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load design plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleToggleActive = async (plan) => {
    const planId = plan.Id || plan.id;
    const planName = plan.Name || plan.name;
    try {
      setError('');
      await updateDesignPlan(planId, { IsActive: !plan.IsActive });
      setSuccess(`Plan "${planName}" ${!plan.IsActive ? 'activated' : 'deactivated'} successfully`);
      await loadPlans();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update plan status');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.plan) return;

    const planId = deleteDialog.plan.Id || deleteDialog.plan.id;
    const planName = deleteDialog.plan.Name || deleteDialog.plan.name;

    try {
      setDeleting(true);
      setError('');
      await deleteDesignPlan(planId);
      setSuccess(`Plan "${planName}" deleted successfully`);
      setDeleteDialog({ open: false, plan: null });
      await loadPlans();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete plan');
      setDeleteDialog({ open: false, plan: null });
    } finally {
      setDeleting(false);
    }
  };

  // ✅ Safe filtering that handles both PascalCase and camelCase field names
  const filteredPlans = plans.filter(plan => {
    const name = (plan.Name || plan.name || '').toLowerCase();
    const description = (plan.Description || plan.description || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || description.includes(query);
  });

  const totalActivePlans = plans.filter(p => p.IsActive === true || p.isActive === true).length;
  const totalInactivePlans = plans.filter(p => p.IsActive === false || p.isActive === false).length;

  // ✅ Helper to get wallet count from plan data
  // Backend returns Wallets array from the include, so we count that
  const getWalletCount = (plan) => {
    if (plan.WalletCount !== undefined && plan.WalletCount !== null) return plan.WalletCount;
    if (plan.walletCount !== undefined && plan.walletCount !== null) return plan.walletCount;
    if (Array.isArray(plan.Wallets)) return plan.Wallets.length;
    if (Array.isArray(plan.wallets)) return plan.wallets.length;
    return 0;
  };

  // ✅ Helper to safely get plan fields
  const getPlanField = (plan, ...fields) => {
    for (const field of fields) {
      if (plan[field] !== undefined && plan[field] !== null) return plan[field];
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#ad1457', 0.08)} 0%, ${alpha('#6a1b9a', 0.08)} 100%)`,
          border: `1px solid ${alpha('#ad1457', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#ad1457', width: 50, height: 50 }}>
              <DesignServicesIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#ad1457">
                {t('DesignPlans') || 'Design Plans'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ManageDesignPlansDesc') || 'Create and manage design subscription plans'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t('Refresh') || 'Refresh'}>
              <IconButton onClick={loadPlans} sx={{ bgcolor: alpha('#ad1457', 0.1) }}>
                <RefreshIcon sx={{ color: '#ad1457' }} />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/design-plans/add')}
              sx={{
                bgcolor: '#ad1457',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { bgcolor: '#880e4f' }
              }}
            >
              {t('AddPlan') || 'Add Plan'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#ad1457', 0.2)}` }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="#ad1457">{plans.length}</Typography>
              <Typography variant="body2" color="text.secondary">{t('TotalPlans') || 'Total Plans'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#2e7d32', 0.2)}` }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="#2e7d32">{totalActivePlans}</Typography>
              <Typography variant="body2" color="text.secondary">{t('Active') || 'Active'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#d32f2f', 0.2)}` }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="#d32f2f">{totalInactivePlans}</Typography>
              <Typography variant="body2" color="text.secondary">{t('Inactive') || 'Inactive'}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search & Filter */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder={t('SearchPlans') || 'Search plans...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1 }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t('ShowInactive') || 'Show Inactive'}
            </Typography>
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              size="small"
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Plans Table */}
      {filteredPlans.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha('#000', 0.2)}` }}>
          <BrushIcon sx={{ fontSize: 64, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchQuery
              ? (t('NoSearchResults') || 'No plans match your search')
              : (t('NoDesignPlans') || 'No design plans found')
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchQuery
              ? (t('TryDifferentSearch') || 'Try a different search term')
              : (t('CreateFirstPlan') || 'Create your first design plan to get started')
            }
          </Typography>
          {!searchQuery && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/design-plans/add')}
              sx={{ bgcolor: '#ad1457', textTransform: 'none', '&:hover': { bgcolor: '#880e4f' } }}
            >
              {t('AddPlan') || 'Add Plan'}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha('#000', 0.08)}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#ad1457', 0.05) }}>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('PlanName') || 'Plan Name'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('Description') || 'Description'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <ImageIcon sx={{ fontSize: 18 }} />
                    <span>{t('Designs') || 'Designs'}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <AttachMoneyIcon sx={{ fontSize: 18 }} />
                    <span>{t('Price') || 'Price'}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                    <span>{t('Wallets') || 'Wallets'}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">{t('Status') || 'Status'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">{t('Actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlans.map((plan, index) => {
                // ✅ Safe field access for both PascalCase and camelCase
                const planId = plan.Id || plan.id;
                const planName = plan.Name || plan.name || '-';
                const planDescription = plan.Description || plan.description || '';
                const planDesigns = plan.NumberOfDesigns || plan.numberOfDesigns || 0;
                const planPrice = plan.Price || plan.price || 0;
                const planCurrency = plan.Currency || plan.currency || 'YER';
                const planFormattedPrice = plan.FormattedPrice || plan.formattedPrice;
                const planIsActive = plan.IsActive === true || plan.isActive === true;
                const walletCount = getWalletCount(plan);

                return (
                  <TableRow
                    key={planId || index}
                    hover
                    sx={{
                      '&:hover': { bgcolor: alpha('#ad1457', 0.02) },
                      opacity: planIsActive ? 1 : 0.6
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{planName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {planDescription || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={planDesigns}
                        size="small"
                        icon={<ImageIcon sx={{ fontSize: 16 }} />}
                        sx={{ bgcolor: alpha('#9c27b0', 0.1), color: '#9c27b0', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700} color="#ad1457">
                        {planFormattedPrice || `${planPrice} ${planCurrency}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={walletCount}
                        size="small"
                        icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          bgcolor: walletCount > 0
                            ? alpha('#1565c0', 0.1)
                            : alpha('#ff9800', 0.1),
                          color: walletCount > 0 ? '#1565c0' : '#e65100',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={planIsActive}
                        onChange={() => handleToggleActive(plan)}
                        size="small"
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t('Edit') || 'Edit'}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/design-plans/edit/${planId}`)}
                            sx={{ color: '#1976d2' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Delete') || 'Delete'}>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDialog({ open: true, plan })}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => {
          if (!deleting) setDeleteDialog({ open: false, plan: null });
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('ConfirmDelete') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('DeletePlanConfirm', { name: deleteDialog.plan?.Name || deleteDialog.plan?.name }) ||
              `Are you sure you want to delete the plan "${deleteDialog.plan?.Name || deleteDialog.plan?.name}"? This action cannot be undone.`
            }
          </DialogContentText>
          {deleteDialog.plan && getWalletCount(deleteDialog.plan) > 0 && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              This plan has {getWalletCount(deleteDialog.plan)} wallet(s) assigned.
              They will be unassigned automatically.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, plan: null })}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            {t('Cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? (t('Deleting') || 'Deleting...') : (t('Delete') || 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}